<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { listAuditLogs, type AuditLogRow } from '../api';

const loading = ref(false);
const rows = ref<AuditLogRow[]>([]);
const total = ref(0);
const filter = ref<{
  targetDomain: '' | 'CERTIFICATION' | 'ROLE_APPLICATION' | 'USER';
  action: '' | 'APPROVE' | 'REJECT' | 'FREEZE' | 'UNFREEZE';
  actorAdminId: string;
  targetId: string;
}>({
  targetDomain: '',
  action: '',
  actorAdminId: '',
  targetId: '',
});
const pageSize = 50;
const skip = ref(0);

async function reload() {
  loading.value = true;
  try {
    const r = await listAuditLogs({
      targetDomain: filter.value.targetDomain || undefined,
      action: filter.value.action || undefined,
      actorAdminId: filter.value.actorAdminId.trim() || undefined,
      targetId: filter.value.targetId.trim() || undefined,
      take: pageSize,
      skip: skip.value,
    });
    rows.value = r.items;
    total.value = r.total;
  } catch (e) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '加载失败');
  } finally {
    loading.value = false;
  }
}

function onSearch() {
  skip.value = 0;
  reload();
}

function onReset() {
  filter.value = { targetDomain: '', action: '', actorAdminId: '', targetId: '' };
  skip.value = 0;
  reload();
}

function onPage(next: number) {
  skip.value = (next - 1) * pageSize;
  reload();
}

function formatTime(s: string): string {
  return new Date(s).toLocaleString('zh-CN');
}

function detailSummary(r: AuditLogRow): string {
  if (!r.detail) return '-';
  // 只做人读摘要,点击详情再展开全 JSON
  const d = r.detail;
  const bits: string[] = [];
  if (typeof d.userId === 'string') bits.push(`user=${d.userId}`);
  if (typeof d.grantedRole === 'string') bits.push(`→ ${d.grantedRole}`);
  if (typeof d.targetRole === 'string') bits.push(`target=${d.targetRole}`);
  if (typeof d.reason === 'string') bits.push(`理由:${(d.reason as string).slice(0, 30)}`);
  if (d.banned) bits.push('(封号)');
  return bits.join(' · ') || JSON.stringify(d).slice(0, 80);
}

function domainLabel(d: string): string {
  return (
    { CERTIFICATION: '证件审核', ROLE_APPLICATION: '身份叠加', USER: '用户' }[d] || d
  );
}

function actionLabel(a: string): string {
  return (
    {
      APPROVE: '批准',
      REJECT: '驳回',
      FREEZE: '冻结',
      UNFREEZE: '解冻',
    }[a] || a
  );
}

function actionTagType(
  a: string,
): 'success' | 'danger' | 'warning' | 'info' {
  return (
    {
      APPROVE: 'success',
      REJECT: 'danger',
      FREEZE: 'warning',
      UNFREEZE: 'info',
    } as Record<string, 'success' | 'danger' | 'warning' | 'info'>
  )[a] || 'info';
}

onMounted(reload);
</script>

<template>
  <div class="audit-logs-page">
    <div class="filter-bar">
      <el-select
        v-model="filter.targetDomain"
        placeholder="所有对象域"
        clearable
        style="width: 160px"
      >
        <el-option label="证件审核" value="CERTIFICATION" />
        <el-option label="身份叠加" value="ROLE_APPLICATION" />
        <el-option label="用户" value="USER" />
      </el-select>
      <el-select
        v-model="filter.action"
        placeholder="所有动作"
        clearable
        style="width: 140px"
      >
        <el-option label="批准" value="APPROVE" />
        <el-option label="驳回" value="REJECT" />
        <el-option label="冻结" value="FREEZE" />
        <el-option label="解冻" value="UNFREEZE" />
      </el-select>
      <el-input
        v-model="filter.actorAdminId"
        placeholder="审核员 ID(精确)"
        clearable
        style="width: 220px"
      />
      <el-input
        v-model="filter.targetId"
        placeholder="对象 ID(精确)"
        clearable
        style="width: 220px"
      />
      <el-button type="primary" @click="onSearch">查询</el-button>
      <el-button @click="onReset">重置</el-button>
      <div class="spacer" />
      <div class="total">共 {{ total }} 条</div>
    </div>

    <el-table :data="rows" v-loading="loading" stripe border style="width: 100%">
      <el-table-column label="时间" width="180">
        <template #default="{ row }">{{ formatTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="审核员" width="180">
        <template #default="{ row }">
          <div>{{ row.actorAdminUsername || '(未知)' }}</div>
          <code class="muted">{{ row.actorAdminId }}</code>
        </template>
      </el-table-column>
      <el-table-column label="对象域" width="120">
        <template #default="{ row }">
          <el-tag size="small">{{ domainLabel(row.targetDomain) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="动作" width="100">
        <template #default="{ row }">
          <el-tag size="small" :type="actionTagType(row.action)">
            {{ actionLabel(row.action) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="对象 ID" width="220">
        <template #default="{ row }">
          <code>{{ row.targetId }}</code>
        </template>
      </el-table-column>
      <el-table-column label="详情">
        <template #default="{ row }">
          <el-tooltip
            v-if="row.detail"
            :content="JSON.stringify(row.detail, null, 2)"
            placement="top"
            raw-content
          >
            <span>{{ detailSummary(row) }}</span>
          </el-tooltip>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        :current-page="Math.floor(skip / pageSize) + 1"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="onPage"
      />
    </div>
  </div>
</template>

<style scoped>
.audit-logs-page { padding: 4px; }
.filter-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.spacer { flex: 1; }
.total { color: #86868b; font-size: 13px; }
.muted { color: #86868b; font-size: 12px; }
.pager { margin-top: 12px; display: flex; justify-content: flex-end; }
</style>
